import React, { useEffect, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import { gql, useMutation } from "@apollo/client";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import { reviewOrder } from "../../apollo/server";
import { BUTTON_RESET, C, FOCUS_RING, FONT_BODY } from "../../utils/designTokens";
import { fieldSx, labelSx } from "../Checkout/checkoutUi";
import RmfDialog, { DialogActions, DialogText, DialogTitle, dialogButtonSx } from "../Dialog/RmfDialog";

const REVIEW_ORDER = gql`${reviewOrder}`;
const WORDS = ["", "Poor", "Fair", "Good", "Very good", "Excellent"];

/** The 5 stars, as a radio group. `size` is the star's pixel size. */
export function StarRating({ value, onChange, size = 28, readOnly = false, label = "Rating" }) {
  const [hover, setHover] = useState(0);
  const shown = hover || value;
  return (
    <Box
      role={readOnly ? "img" : "radiogroup"}
      aria-label={readOnly ? `Rated ${value} ${value === 1 ? "star" : "stars"}` : label}
      sx={{ display: "inline-flex", gap: 0.25 }}
      onMouseLeave={() => setHover(0)}
    >
      {[1, 2, 3, 4, 5].map((n) => {
        const on = n <= shown;
        const icon = <StarRoundedIcon aria-hidden="true" sx={{ fontSize: size, color: on ? C.goldAccent : C.borderStrong, transition: "color .1s ease" }} />;
        if (readOnly) return <Box key={n} component="span" sx={{ display: "inline-flex" }}>{icon}</Box>;
        return (
          <Box
            key={n}
            component="button"
            type="button"
            role="radio"
            aria-checked={value === n}
            aria-label={`${n} ${n === 1 ? "star" : "stars"}`}
            tabIndex={value ? (value === n ? 0 : -1) : n === 1 ? 0 : -1}
            onMouseEnter={() => setHover(n)}
            onFocus={() => setHover(0)}
            onClick={() => onChange(n)}
            onKeyDown={(e) => {
              const step = e.key === "ArrowRight" || e.key === "ArrowUp" ? 1 : e.key === "ArrowLeft" || e.key === "ArrowDown" ? -1 : 0;
              if (!step) return;
              e.preventDefault();
              const next = Math.min(5, Math.max(1, (value || 0) + step));
              onChange(next, { keyboard: true });
              e.currentTarget.parentElement.children[next - 1]?.focus();
            }}
            sx={{ ...BUTTON_RESET, ...FOCUS_RING, display: "inline-flex", borderRadius: "6px", p: "1px", "&:hover": { transform: "scale(1.08)" } }}
          >
            {icon}
          </Box>
        );
      })}
    </Box>
  );
}

/** "Rate your order": stars plus an optional note (the reviewOrder mutation). */
function ReviewDialog({ open, onClose, orderId, restaurantName, initialRating = 0, onDone }) {
  const [rating, setRating] = useState(initialRating);
  const [description, setDescription] = useState("");
  const [error, setError] = useState("");
  useEffect(() => {
    if (open) {
      setRating(initialRating);
      setError("");
    }
  }, [open, initialRating]);

  const [mutate, { loading }] = useMutation(REVIEW_ORDER, {
    onCompleted: () => {
      onDone?.(rating);
      onClose();
    },
    onError: (err) => setError(err.message || "Couldn't send your review. Please try again."),
  });

  return (
    <RmfDialog open={open} onClose={() => !loading && onClose()}>
      <DialogTitle>Rate your order</DialogTitle>
      {restaurantName && <DialogText sx={{ mt: 0.5 }}>{restaurantName}</DialogText>}
      <Box sx={{ mt: 2.5, textAlign: "center" }}>
        <StarRating value={rating} onChange={setRating} size={40} label="Your rating" />
        <Box sx={{ mt: 0.5, minHeight: 20, fontFamily: FONT_BODY, fontSize: 14, fontWeight: 600, color: C.muted }}>{WORDS[rating]}</Box>
      </Box>
      <Box component="label" htmlFor={`review-note-${orderId}`} sx={{ ...labelSx, display: "block", mt: 2, mb: 0.75 }}>
        What did you think? (optional)
      </Box>
      <Box
        component="textarea"
        id={`review-note-${orderId}`}
        rows={3}
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="More detail helps other customers"
        sx={{ ...fieldSx, height: "auto", py: 1.25, resize: "vertical" }}
      />
      {error && <Box role="alert" sx={{ mt: 1.5, fontFamily: FONT_BODY, fontSize: 14, color: C.red }}>{error}</Box>}
      <DialogActions>
        <Box
          component="button"
          type="button"
          disabled={rating < 1 || loading}
          aria-busy={loading}
          onClick={() => mutate({ variables: { order: orderId, rating, description: description.trim() } })}
          sx={dialogButtonSx.primary}
        >
          {loading ? <CircularProgress size={20} sx={{ color: "inherit" }} /> : "Submit review"}
        </Box>
        <Box component="button" type="button" onClick={onClose} disabled={loading} sx={dialogButtonSx.secondary}>
          Not now
        </Box>
      </DialogActions>
    </RmfDialog>
  );
}

export default ReviewDialog;
